import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronDownIcon, ArrowLeftIcon } from "@heroicons/react/24/outline";
import Input from "../Shared/Input";
import PhoneNumberInput from "../Shared/PhoneNumberInput";
import AddressLookupNative from "../Shared/AddressLookupNative";
import TextArea from "../Shared/TextArea";
import AlertRed from "../Shared/AlertRed";
import { saveClient } from "../../api/clientApi";
import { usePhoneNumber } from "../../hooks/usePhoneNumber";
import { validatePhoneNumber, formatToE164 } from "../../utility/phoneNumberUtils";
import {
  createTrimHandler,
  EMAIL_REGEX,
} from "../../utility/formValidationUtils";
import { useToast } from "../../hooks/useToast";
import { mapApiError } from "../../utils/formErrorMapper";

const CreateClientForm = ({ defaultName = "", onClientCreated, onCancel, onDirtyChange, cancelLabel = "Cancel" }) => {
  const [showMoreFields, setShowMoreFields] = useState(false);
  const [banner, setBanner] = useState(null);
  const [noteCharCount, setNoteCharCount] = useState(0);
  const maxCharacters = 500;

  const queryClient = useQueryClient();
  const toast = useToast();

  const nameParts = defaultName.trim().split(/\s+/);
  const defaultFirstName = nameParts[0] || "";
  const defaultLastName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isDirty },
    setError,
  } = useForm({
    defaultValues: {
      firstName: defaultFirstName,
      lastName: defaultLastName,
      email: "",
      phoneNumber: "",
      serviceAddress: undefined,
      note: "",
    },
  });

  const { selectedCountry, onCountryChangeHandler, onInputChange, defaultCountry } = usePhoneNumber(
    ""
  );

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const { mutateAsync: mutateCreateClient, isPending } = useMutation({
    mutationFn: saveClient,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["clientList"] });
      queryClient.invalidateQueries({ queryKey: ["clientListTotal"] });
      toast.success("Client Added", "New client has been added successfully");
      onClientCreated(data);
    },
  });

  const [lastSubmitPayload, setLastSubmitPayload] = useState(null);

  const runSubmit = async (data) => {
    if (isPending) return;
    setBanner(null);
    setLastSubmitPayload(data);
    try {
      let payload = data;
      if (data.phoneNumber) {
        const isValid = validatePhoneNumber(data.phoneNumber, setError, defaultCountry);
        if (!isValid) return;
        payload = {
          ...data,
          phoneNumber: formatToE164(data.phoneNumber, defaultCountry),
        };
      }
      await mutateCreateClient(payload);
    } catch (err) {
      const result = mapApiError(err, { setError });
      if (result.handled) return;
      if (result.kind === "auth") return;
      setBanner(result.banner);
    }
  };

  const onSelectAddress = (address) => {
    setValue(
      "serviceAddress",
      address ? { full: address.name, placeId: address.id } : undefined,
      { shouldDirty: true }
    );
  };

  const handleCancel = () => {
    onDirtyChange?.(false);
    onCancel();
  };

  const handleRetry = () => {
    if (lastSubmitPayload) runSubmit(lastSubmitPayload);
  };

  return (
    <form onSubmit={handleSubmit(runSubmit)}>
      {banner ? (
        <div className="mb-4">
          <AlertRed
            title={banner.title}
            message={banner.message}
            onRetry={handleRetry}
          />
        </div>
      ) : null}
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Input
            id="createClient-firstName"
            name="firstName"
            label="First name"
            placeholder="e.g., Jane"
            errormessage={errors?.firstName?.message}
            {...register("firstName", {
              onBlur: createTrimHandler(setValue, "firstName"),
              required: "First name is required",
            })}
          />
          <Input
            id="createClient-lastName"
            name="lastName"
            label="Last name"
            optional
            placeholder="e.g., Doe"
            errormessage={errors?.lastName?.message}
            {...register("lastName", {
              onBlur: createTrimHandler(setValue, "lastName"),
            })}
          />
        </div>
        <Input
          id="createClient-email"
          name="email"
          label="Email"
          placeholder="name@example.com"
          errormessage={errors?.email?.message}
          {...register("email", {
            onBlur: createTrimHandler(setValue, "email"),
            required: "Email is required",
            pattern: {
              value: EMAIL_REGEX,
              message: "Invalid email address",
            },
          })}
        />

        <button
          type="button"
          onClick={() => setShowMoreFields(!showMoreFields)}
          className="flex items-center gap-1 text-sm font-medium text-ink-500 hover:text-ink-700 transition-colors"
        >
          <ChevronDownIcon
            className={`h-4 w-4 transition-transform duration-200 ${
              showMoreFields ? "rotate-180" : ""
            }`}
          />
          {showMoreFields ? "Show fewer fields" : "More fields"}
        </button>

        {showMoreFields && (
          <div className="space-y-4">
            <PhoneNumberInput
              selectedCountry={selectedCountry}
              onCountryChangeHandler={onCountryChangeHandler}
              onInputChange={onInputChange}
              optional={true}
              errormessage={errors?.phoneNumber?.message}
              {...register("phoneNumber", {
                onBlur: createTrimHandler(setValue, "phoneNumber"),
              })}
            />
            <AddressLookupNative
              key="createClient-address"
              label="Address"
              optional
              placeholder="Search for an address..."
              onSelect={onSelectAddress}
            />
            <TextArea
              label="Notes"
              optional
              id="createClient-note"
              name="note"
              placeholder="Anything the team should know (allergies, access codes, preferences)"
              errormessage={errors?.note?.message}
              charCount={noteCharCount}
              maxLength={maxCharacters}
              {...register("note", {
                maxLength: {
                  value: maxCharacters,
                  message: `Notes cannot exceed ${maxCharacters} characters`,
                },
                onBlur: (e) => {
                  setValue("note", e.target.value.trim(), {
                    shouldValidate: true,
                  });
                },
                onChange: (e) => {
                  setNoteCharCount(e.target.value.trim().length);
                  setValue("note", e.target.value, {
                    shouldValidate: true,
                  });
                },
              })}
            />
          </div>
        )}
      </div>

      <div className="mt-6 flex justify-between gap-3 border-t border-ink-100 pt-4">
        <button
          type="button"
          onClick={handleCancel}
          disabled={isPending}
          className="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-ink-900 shadow-sm ring-1 ring-inset ring-ink-200 hover:bg-cream-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {cancelLabel !== "Cancel" && <ArrowLeftIcon className="h-4 w-4" />}
          {cancelLabel}
        </button>
        <button
          type="submit"
          disabled={isPending}
          className="rounded-md bg-accent-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-accent-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isPending ? "Saving…" : "Create client"}
        </button>
      </div>
    </form>
  );
};

export default CreateClientForm;
